import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Label,
} from "reactstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axiosInstance from "Axiosinstance";

const fieldClasses = (errors, touched) =>
  "form-control" + (errors && touched ? " is-invalid" : "");

// Password is required only when creating a new user. On edit it's optional —
// leave it blank to keep the existing password.
const buildSchema = (mode) =>
  Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .max(25, "First name must be 25 characters or fewer")
      .required("First name is required"),
    lastName: Yup.string()
      .trim()
      .max(25, "Last name must be 25 characters or fewer")
      .required("Last name is required"),
    email: Yup.string()
      .trim()
      .email("Enter a valid email address")
      .required("Email is required"),
    password:
      mode === "edit"
        ? Yup.string()
            .trim()
            .transform((v) => (v === "" ? undefined : v))
            .matches(
              /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&^#()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
              "Password must be at least 8 characters and include uppercase, lowercase, number, and special character"
            )
            .notRequired()
        : Yup.string()
            .trim()
            .matches(
              /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&^#()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
              "Password must be at least 8 characters and include uppercase, lowercase, number, and special character"
            )
            .required("Password is required"),
    phone: Yup.string()
      .trim()
      .matches(/^\d{10}$/, "Enter a valid 10-digit phone number")
      .required("Phone is required"),
    countryCode: Yup.string()
      .oneOf(["+91"], "Country code must be +91")
      .required("Country code is required"),
    branch_uid: Yup.string().trim().required("Branch is required"),
  });

/**
 * Generates a random 8-character password in the pattern: Word + Special + 3 digits
 * e.g. "Test@123", "Blue#482", "Gold!907"
 * This always satisfies the password regex above:
 * - 1 uppercase (first letter of the word)
 * - lowercase letters (rest of the word)
 * - 1 special character
 * - digits
 * - length 8
 */
const generateRandomPassword = () => {
  const words = [
    "Test",
    "Best",
    "Cool",
    "Nice",
    "Fast",
    "Blue",
    "Gold",
    "Star",
    "Moon",
    "Rock",
    "Wave",
    "Fire",
  ];
  const specials = ["@", "$", "!", "%", "*", "?", "&", "#"];

  const word = words[Math.floor(Math.random() * words.length)];
  const special = specials[Math.floor(Math.random() * specials.length)];
  const digits = Math.floor(100 + Math.random() * 900); // 3-digit number, 100-999

  return `${word}${special}${digits}`;
};

// Simple inline icons so we don't need to pull in an icon library.
const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a20.3 20.3 0 0 1 5.06-6.06M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a20.3 20.3 0 0 1-2.16 3.19" />
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

/**
 * Shared modal for both creating and editing a branch user.
 *
 * Props:
 * - isOpen, toggle
 * - mode: "create" | "edit"
 * - initialData: the branch user record when mode === "edit"
 * - company: the parent company object (needs company_uid + branches)
 * - onSuccess: called after a successful create/update so parent can refetch
 *
 * NOTE: the update call assumes `PUT /api/branchuser/:uid`. Adjust the
 * endpoint/method below if your backend uses something different.
 */
const BranchUserFormModal = ({
  isOpen,
  toggle,
  mode = "create",
  initialData,
  company,
  onSuccess,
}) => {
  const isEdit = mode === "edit";
  const branchOptions = company?.branches || [];

  const [showPassword, setShowPassword] = useState(false);
  const [generatingPassword, setGeneratingPassword] = useState(false);

  const initialValues = {
    firstName: isEdit ? initialData?.first_name || "" : "",
    lastName: isEdit ? initialData?.last_name || "" : "",
    email: isEdit ? initialData?.email || "" : "",
    password: "",
    phone: isEdit ? initialData?.phone || "" : "",
    countryCode: "+91",
    branch_uid: isEdit ? initialData?.branch_uid || "" : "",
  };

  // Generates a password, shows a brief "Generating..." loading state on the
  // button, then fills it into the Formik field and reveals it in the input.
  const handleGeneratePassword = (setFieldValue, setFieldTouched) => {
    setGeneratingPassword(true);
    setTimeout(() => {
      const newPassword = generateRandomPassword();
      setFieldValue("password", newPassword, true);
      setFieldTouched("password", true, false);
      setShowPassword(true);
      setGeneratingPassword(false);
    }, 500);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        countryCode: values.countryCode,
        branch_uid: values.branch_uid,
        company_uid: company?.company_uid,
        type: "partner",
      };

      // Only send password if the user actually typed one.
      if (values.password) {
        payload.password = values.password;
      }

      let data;
      if (isEdit) {
        payload.accessPermission = initialData?.activitiesAccess || [];
        ({ data } = await axiosInstance.put(
          `/api/branchuser/${initialData.uid}`,
          payload
        ));
      } else {
        payload.accessPermission = [];
        ({ data } = await axiosInstance.post("/api/branchuser", payload));
      }

      if (data?.status && !data?.isError) {
        toast.success(
          data?.message ||
            data?.msg ||
            (isEdit
              ? "Branch user updated successfully"
              : "Branch user created successfully")
        );
        resetForm();
        setShowPassword(false);
        toggle();
        onSuccess?.();
      } else {
        toast.error(
          data?.message ||
            data?.error ||
            (isEdit
              ? "Unable to update branch user"
              : "Unable to create branch user")
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          (isEdit
            ? "Unable to update branch user"
            : "Unable to create branch user")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {isEdit ? "Edit Branch User" : "Create Branch User"}
      </ModalHeader>
      <ModalBody>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={buildSchema(mode)}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting, setFieldValue, setFieldTouched }) => (
            <Form>
              <Row>
                <Col md="6">
                  <div className="mb-3">
                    <Label className="form-label">First Name *</Label>
                    <Field
                      name="firstName"
                      type="text"
                      maxLength="25"
                      className={fieldClasses(errors.firstName, touched.firstName)}
                    />
                    <ErrorMessage
                      name="firstName"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </Col>
                <Col md="6">
                  <div className="mb-3">
                    <Label className="form-label">Last Name *</Label>
                    <Field
                      name="lastName"
                      type="text"
                      maxLength="25"
                      className={fieldClasses(errors.lastName, touched.lastName)}
                    />
                    <ErrorMessage
                      name="lastName"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <div className="mb-3">
                    <Label className="form-label">Email *</Label>
                    <Field
                      name="email"
                      type="email"
                      className={fieldClasses(errors.email, touched.email)}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </Col>
                <Col md="6">
                  <div className="mb-3">
                    <Label className="form-label">
                      Password {isEdit ? "" : "*"}
                    </Label>
                    <div className="input-group">
                      <Field
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={
                          isEdit
                            ? "Leave blank to keep current password"
                            : "Click Generate Password"
                        }
                        className={fieldClasses(errors.password, touched.password)}
                        autoComplete="new-password"
                        readOnly
                        // Manual typing is disabled on purpose — the password can
                        // only be set via the Generate Password button below, and
                        // clicking it again simply overwrites it with a new one.
                        onKeyDown={(e) => e.preventDefault()}
                      />
                      <Button
                        type="button"
                        color="light"
                        className="border"
                        onClick={() => setShowPassword((prev) => !prev)}
                        title={showPassword ? "Hide password" : "Show password"}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </Button>
                      <Button
                        type="button"
                        color="light"
                        className="border"
                        style={{ fontSize: "0.8rem" }}
                        onClick={() =>
                          handleGeneratePassword(setFieldValue, setFieldTouched)
                        }
                        disabled={generatingPassword}
                      >
                        {generatingPassword ? "Generating..." : "Generate Password"}
                      </Button>
                    </div>
                    <small className="text-muted d-block">
                      Password can only be set using the Generate Password button.
                      Click it again to create a new one.
                    </small>
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md="4">
                  <div className="mb-3">
                    <Label className="form-label">Country Code *</Label>
                    <Field
                      name="countryCode"
                      type="text"
                      className={fieldClasses(errors.countryCode, touched.countryCode)}
                      value="+91"
                      readOnly
                    />
                    <ErrorMessage
                      name="countryCode"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </Col>
                <Col md="8">
                  <div className="mb-3">
                    <Label className="form-label">Phone *</Label>
                    <Field name="phone">
                      {({ field, form }) => (
                        <input
                          {...field}
                          type="tel"
                          inputMode="numeric"
                          maxLength="10"
                          className={fieldClasses(errors.phone, touched.phone)}
                          onChange={(e) => {
                            form.setFieldValue(
                              field.name,
                              e.target.value.replace(/\D/g, "").slice(0, 10)
                            );
                          }}
                          onKeyDown={(e) => {
                            const allowedKeys = [
                              "Backspace",
                              "Delete",
                              "Tab",
                              "ArrowLeft",
                              "ArrowRight",
                              "Home",
                              "End",
                            ];
                            if (
                              allowedKeys.includes(e.key) ||
                              (e.ctrlKey && ["a", "c", "v", "x"].includes(e.key.toLowerCase()))
                            ) {
                              return;
                            }
                            if (!/^\d$/.test(e.key)) e.preventDefault();
                          }}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="phone"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <div className="mb-3">
                    <Label className="form-label">Branch *</Label>
                    <Field
                      name="branch_uid"
                      as="select"
                      className={fieldClasses(errors.branch_uid, touched.branch_uid)}
                      disabled={!company || branchOptions.length === 0}
                    >
                      <option value="">
                        {company
                          ? branchOptions.length > 0
                            ? "Select branch"
                            : "No branches available"
                          : "Loading company..."}
                      </option>
                      {branchOptions.map((branch) => (
                        <option
                          key={branch.branch_uid || branch._id}
                          value={branch.branch_uid || branch._id}
                        >
                          {branch.branch_name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="branch_uid"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </Col>
              </Row>

              <ModalFooter>
                <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  disabled={isSubmitting || !company}
                >
                  {isSubmitting
                    ? isEdit
                      ? "Saving..."
                      : "Creating..."
                    : isEdit
                    ? "Save Changes"
                    : "Create User"}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default BranchUserFormModal;
